import { EditorStarterItem } from '../items/item-type';
import { TrackType } from '../state/types';
import { isTrackPositionBusy } from './is-track-position-busy';
import { getZoneBoundaryIndex, getZoneForItemType } from './zone-utils';

export interface Space {
  trackIndex: number;
  forceCreateNewTrack: boolean;
}

export type FindSpaceStartPosition =
  | {
      type: 'front';
    }
  | {
      type: 'back';
    }
  | {
      type: 'directly-above';
      trackIndex: number;
    }
  | {
      /** 특정 트랙에 직접 배치 (가능하면 해당 트랙에, 불가능하면 새 트랙 생성) */
      type: 'on-track';
      trackIndex: number;
    };

const findSpaceForItemAbove = ({
  durationInFrames,
  startAt,
  tracks,
  above,
  zoneStart,
  zoneEnd,
  items,
}: {
  durationInFrames: number;
  startAt: number;
  tracks: TrackType[];
  above: number;
  zoneStart: number;
  zoneEnd: number;
  items: Record<string, EditorStarterItem>;
}): Space => {
  if (zoneStart > zoneEnd) {
    return { trackIndex: zoneStart, forceCreateNewTrack: true };
  }

  if (above <= zoneStart) {
    if (zoneStart === 0) {
      return { trackIndex: -1, forceCreateNewTrack: false };
    }
    return { trackIndex: zoneStart, forceCreateNewTrack: true };
  }

  const trackAboveIndex = Math.max(zoneStart, above - 1);
  const trackAbove = tracks[trackAboveIndex];

  if (
    isTrackPositionBusy({
      track: trackAbove ?? (null as unknown as TrackType),
      startAt,
      durationInFrames,
      items,
    })
  ) {
    const insertIndex = Math.min(Math.max(above, zoneStart), zoneEnd + 1);
    return { trackIndex: insertIndex, forceCreateNewTrack: true };
  }

  return { trackIndex: trackAboveIndex, forceCreateNewTrack: false };
};

/**
 * 특정 트랙에 직접 배치를 시도합니다.
 * - 트랙이 비어있으면 해당 트랙에 배치
 * - 트랙이 사용 중이면 해당 트랙 위에 새 트랙을 생성
 */
const findSpaceForItemOnTrack = ({
  durationInFrames,
  startAt,
  tracks,
  trackIndex,
  zoneStart,
  zoneEnd,
  items,
}: {
  durationInFrames: number;
  startAt: number;
  tracks: TrackType[];
  trackIndex: number;
  zoneStart: number;
  zoneEnd: number;
  items: Record<string, EditorStarterItem>;
}): Space => {
  if (zoneStart > zoneEnd) {
    return { trackIndex: zoneStart, forceCreateNewTrack: true };
  }

  const clampedTrackIndex = Math.min(Math.max(trackIndex, zoneStart), zoneEnd);

  // 트랙이 존재하지 않으면 새 트랙 생성
  if (
    trackIndex < zoneStart ||
    trackIndex > zoneEnd ||
    trackIndex >= tracks.length
  ) {
    const insertIndex =
      trackIndex > zoneEnd ? zoneEnd + 1 : Math.max(trackIndex, zoneStart);
    return { trackIndex: insertIndex, forceCreateNewTrack: true };
  }

  const track = tracks[clampedTrackIndex];

  const isBusy = isTrackPositionBusy({
    track: track ?? (null as unknown as TrackType),
    startAt,
    durationInFrames,
    items,
  });

  if (isBusy) {
    // 해당 트랙 위에 새 트랙 생성
    return { trackIndex: clampedTrackIndex, forceCreateNewTrack: true };
  }

  // 해당 트랙에 직접 배치
  return { trackIndex: clampedTrackIndex, forceCreateNewTrack: false };
};

const findSpaceForItemInFront = ({
  durationInFrames,
  startAt,
  tracks,
  zoneStart,
  zoneEnd,
  zoneFallbackCreateIndex,
  items,
  stopOnFirstFound,
}: {
  durationInFrames: number;
  startAt: number;
  tracks: TrackType[];
  zoneStart: number;
  zoneEnd: number;
  zoneFallbackCreateIndex: number;
  items: Record<string, EditorStarterItem>;
  stopOnFirstFound: boolean;
}): Space => {
  if (zoneStart > zoneEnd) {
    return {
      trackIndex: zoneFallbackCreateIndex,
      forceCreateNewTrack: true,
    };
  }

  // 더 나은 곳을 찾지 못하면 새 track을 생성합니다
  let bestTrackPosition: Space = {
    trackIndex: zoneFallbackCreateIndex,
    forceCreateNewTrack: true,
  };

  for (let i = zoneStart; i <= zoneEnd; i += 1) {
    const track = tracks[i];
    const isBusy = isTrackPositionBusy({
      track: track ?? (null as unknown as TrackType),
      startAt,
      durationInFrames,
      items,
    });

    // track이 사용 중이면 다른 item 아래에 배치하게 됩니다.
    // 여기서 중단하고 지금까지 찾은 최적의 track 위치를 반환합니다.
    if (isBusy) {
      return bestTrackPosition;
    }
    bestTrackPosition = { trackIndex: i, forceCreateNewTrack: false };
    if (stopOnFirstFound) {
      break;
    }
  }

  return bestTrackPosition;
};

const findSpaceForItemInBack = ({
  durationInFrames,
  startAt,
  tracks,
  zoneStart,
  zoneEnd,
  zoneFallbackCreateIndex,
  items,
  stopOnFirstFound,
}: {
  durationInFrames: number;
  startAt: number;
  tracks: TrackType[];
  zoneStart: number;
  zoneEnd: number;
  zoneFallbackCreateIndex: number;
  items: Record<string, EditorStarterItem>;
  stopOnFirstFound: boolean;
}) => {
  if (zoneStart > zoneEnd) {
    return {
      trackIndex: zoneFallbackCreateIndex,
      forceCreateNewTrack: true,
    };
  }

  // 더 나은 곳을 찾지 못하면 새 track을 생성합니다
  let bestTrackPosition: Space = {
    trackIndex: zoneFallbackCreateIndex,
    forceCreateNewTrack: true,
  };

  for (let i = zoneEnd; i >= zoneStart; i -= 1) {
    const track = tracks[i];
    const isBusy = isTrackPositionBusy({
      track: track ?? (null as unknown as TrackType),
      startAt,
      durationInFrames,
      items,
    });

    // track이 사용 중이면 다른 item 아래에 배치하게 됩니다.
    // 여기서 중단하고 지금까지 찾은 최적의 track 위치를 반환합니다.
    if (isBusy) {
      return bestTrackPosition;
    }
    bestTrackPosition = { trackIndex: i, forceCreateNewTrack: false };
    if (stopOnFirstFound) {
      break;
    }
  }

  return bestTrackPosition;
};

export const findSpaceForItem = ({
  durationInFrames,
  startAt,
  tracks,
  startPosition,
  stopOnFirstFound,
  items,
  itemType,
}: {
  durationInFrames: number;
  startAt: number;
  tracks: TrackType[];
  startPosition: FindSpaceStartPosition;
  stopOnFirstFound: boolean;
  items: Record<string, EditorStarterItem>;
  itemType: EditorStarterItem['type'];
}): Space => {
  const zone = getZoneForItemType(itemType);
  const zoneBoundaryIndex = getZoneBoundaryIndex(tracks);
  const zoneStart = zone === 'audio' ? zoneBoundaryIndex : 0;
  const zoneEnd = zone === 'audio' ? tracks.length - 1 : zoneBoundaryIndex - 1;
  const zoneFrontCreateIndex = zone === 'audio' ? zoneBoundaryIndex : -1;
  const zoneBackCreateIndex =
    zone === 'audio' ? tracks.length : zoneBoundaryIndex;

  if (startPosition.type === 'directly-above') {
    return findSpaceForItemAbove({
      durationInFrames,
      startAt,
      tracks,
      above: startPosition.trackIndex,
      zoneStart,
      zoneEnd,
      items,
    });
  }

  if (startPosition.type === 'on-track') {
    return findSpaceForItemOnTrack({
      durationInFrames,
      startAt,
      tracks,
      trackIndex: startPosition.trackIndex,
      zoneStart,
      zoneEnd,
      items,
    });
  }

  if (startPosition.type === 'front') {
    // 오디오는 visual과 다르게 위로 쌓지 않고 아래로 깔아야 하므로,
    // front 요청이어도 audio zone에서는 back 규칙으로 배치한다.
    if (zone === 'audio') {
      return findSpaceForItemInBack({
        durationInFrames,
        startAt,
        tracks,
        zoneStart,
        zoneEnd,
        zoneFallbackCreateIndex: zoneBackCreateIndex,
        items,
        stopOnFirstFound,
      });
    }

    return findSpaceForItemInFront({
      durationInFrames,
      startAt,
      tracks,
      zoneStart,
      zoneEnd,
      zoneFallbackCreateIndex: zoneFrontCreateIndex,
      items,
      stopOnFirstFound,
    });
  }

  if (startPosition.type === 'back') {
    return findSpaceForItemInBack({
      durationInFrames,
      startAt,
      tracks,
      zoneStart,
      zoneEnd,
      zoneFallbackCreateIndex: zoneBackCreateIndex,
      items,
      stopOnFirstFound,
    });
  }

  throw new Error(
    'Invalid start position: ' + JSON.stringify(startPosition satisfies never),
  );
};
